import { ElementRef, Injectable } from "@angular/core";
import { Data } from "@angular/router";
import { AuthSyndicateService } from "@core/AuthServices/authSyndicate/auth-syndicate.service";
import { MixpanelService } from "src/app/services/mixpanel.service";
import { PaymentService } from "src/app/services/payment.service";

export interface CheckoutEventBody extends Data {
  Lottery_Product?: string;
  Dwell_Time?: string;
  Payment_Method?: string;
  Draw_Days?: string;
  Line_Count?: number | string;
  Total_Amount?: number | string;
  Journey_ID?: number | string;
  For_how_many_weeks?: string;
  Extension_Period?: string;
  Pre_Existing_Card?: "Yes" | "No" | string;
  Offer_ID?: string | number;
  CTA?: string;
  P_Type?: string;
  Destination_URL?: string;
}

export interface CheckoutTrackingOptions {
  journeyId: number | string;
  preExistingCard?: boolean;
  cardCount?: number;
  offerId?: string | number;
  cta?: string;
  pType?: string;
  includeDestinationUrl?: boolean;
}

@Injectable()
export class CheckoutTrackingHelper {
  constructor(
    private mixPanelService: MixpanelService,
    private authSyndicateService: AuthSyndicateService
  ) {}

  buildCheckoutEventBody(data: Data, options: CheckoutTrackingOptions): CheckoutEventBody {
    const now = new Date();
    const dwellTime = 0; // existing implementation used start/end at call-time
    const formattedDwellTime = this.authSyndicateService.convertMillisecondsToTime(dwellTime);

    const cardCount = typeof options.cardCount === "number" ? options.cardCount : 0;
    const preExistingCard =
      typeof options.preExistingCard === "boolean"
        ? options.preExistingCard
        : cardCount > 0;

    const destinationUrl =
      options.includeDestinationUrl ?? false ? this.getDestinationUrl() : undefined;

    const body: CheckoutEventBody = {
      Lottery_Product: data?.real_name || "",
      Dwell_Time: formattedDwellTime,
      Payment_Method: data?.offers?.payment_type || "",
      Draw_Days: data?.offers?.draw_day || "",
      Line_Count: data?.offers?.lines || "",
      Total_Amount: data?.offers?.price || "",
      Journey_ID: options.journeyId,
      For_how_many_weeks:
        data?.selectedDiscount?.title === "No extension"
          ? "Only once"
          : data?.selectedDiscount?.title || "",
      Extension_Period: data?.selectedDiscount?.title || "",
      Pre_Existing_Card: preExistingCard ? "Yes" : "No",
      Offer_ID: options.offerId ?? data?.offers?.offerid ?? "",
    };

    if (options.cta) {
      body.CTA = options.cta;
    }

    if (options.pType) {
      body.P_Type = options.pType;
    }

    if (destinationUrl) {
      body.Destination_URL = destinationUrl;
    }

    return body;
  }

  trackCheckoutViewed(data: Data, options: CheckoutTrackingOptions): void {
    const body = this.buildCheckoutEventBody(data, options);
    this.mixPanelService.GTMPopupInitializationForInboxNew("Checkout Popup Viewed", body);
  }

  trackCheckoutClicked(data: Data, options: CheckoutTrackingOptions): void {
    const body = this.buildCheckoutEventBody(data, options);
    this.mixPanelService.GTMPopupInitializationForInboxNew("Checkout Popup Clicked", body);
  }

  trackPaymentStatus(data: Data, options: CheckoutTrackingOptions): CheckoutEventBody {
    return this.buildCheckoutEventBody(data, {
      ...options,
      includeDestinationUrl: true,
    });
  }

  trackCheckoutViewedForCards(
    data: Data,
    journeyId: number | string,
    cardCount: number,
    offerId?: string | number
  ): void {
    this.trackCheckoutViewed(data, {
      journeyId,
      cardCount,
      offerId: offerId ?? data?.offers?.offerid ?? "",
    });
  }

  trackPayNowClicked(data: Data, journeyId: number | string): void {
    this.trackCheckoutClicked(data, {
      journeyId,
      preExistingCard: true,
      offerId: data?.offers?.offerid ?? "",
      cta: "Pay now",
    });
  }

  getPayNowPaymentStatusEventBody(
    data: Data,
    journeyId: number | string,
    cardCount: number,
    pType: string
  ): CheckoutEventBody {
    return this.trackPaymentStatus(data, {
      journeyId,
      cardCount,
      cta: "Pay now",
      pType,
    });
  }

  trackPaymentStatusForPurchase(
    paymentService: PaymentService,
    cpId: string,
    data: Data,
    journeyId: number | string,
    cardCount: number,
    pType: string
  ): void {
    const eventbody = this.getPayNowPaymentStatusEventBody(data, journeyId, cardCount, pType);
    paymentService.trackPaymentStatus(cpId, eventbody);
  }

  calculateScrollDepth(elementRef: ElementRef, sectionId: string): number {
    const sectionElement = elementRef?.nativeElement?.querySelector(`#${sectionId}`);
    if (!sectionElement) {
      return 0;
    }

    const sectionHeight = sectionElement.offsetHeight;
    const sectionTop = sectionElement.getBoundingClientRect().top;
    const windowHeight = window.innerHeight;
    const sectionBottom = sectionTop + sectionHeight;

    if (sectionTop >= 0 && sectionBottom <= windowHeight) {
      return 100;
    } else if (sectionTop < 0 && sectionBottom > 0) {
      return (sectionBottom / sectionHeight) * 100;
    } else if (sectionTop < windowHeight && sectionBottom > windowHeight) {
      return ((windowHeight - sectionTop) / sectionHeight) * 100;
    }

    return 0;
  }

  persistScrollDepth(elementRef: ElementRef, sectionId: string, storageKey = "scrollDepthValue"): string {
    const scrollDepth = this.calculateScrollDepth(elementRef, sectionId).toFixed(2);
    localStorage.setItem(storageKey, scrollDepth);
    return scrollDepth;
  }

  private getDestinationUrl(): string {
    const pType = localStorage.getItem("payType");

    if (pType === "cash") {
      return `${window.location.origin}/mainTabs/tabs/syndicate`;
    }

    if (pType === "cash-issue") {
      return `${window.location.origin}/mainTabs/tabs/store`;
    }

    return `${window.location.origin}/mainTabs/tabs/syndicate`;
  }
}


